import { useEffect, useState, useRef, useMemo } from 'react'
import { Layout, Row, Col, Tabs, Spin } from 'antd'
import { CaretLeftOutlined, CaretRightOutlined } from '@ant-design/icons'
import { Outlet } from 'react-router-dom'
import SeasonRuleList from './Components/SeasonRuleList'
import { getSeasonRules, getSeasons } from '../../Services/Omnichannel/SeasonRuleService'
import { getChannelFilters } from '../../Services/Omnichannel/ChannelService'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { getLocks } from '../../Services/Omnichannel/LockService'
import { TypeOfChannel, setTypeOfChannel, iconDictionary } from 'Slices/Omnichannel/DiscountSlice'
import { getInfoByChannelType, renderTranslation } from 'Helpers/GenericHelpers'
import { TranslationKeys } from 'Constants/TranslationKeys'
import { checkDefaultTypeOfChannel } from 'Helpers/ConfigurationHelper'

const { Content } = Layout

function Omnichannel() {
  const dispatch = useAppDispatch()
  const language = useAppSelector(state => state.present.language.value)
  const sites = useAppSelector(state => state.present.configuration.sites)
  const stores = useAppSelector(state => state.present.configuration.stores)
  const marketplace = useAppSelector(state => state.present.configuration.marketplace)
  const typeOfChannel: TypeOfChannel = useAppSelector(state => state.present.discount.typeOfChannel)
  const isSingleTab = useAppSelector(state => state.present.configuration.isOmniPricesSingleTab)
  const publicWorkspace = useAppSelector(state => state.present.configuration.publicWorkspace)
  const isOmnichannelAndPricesVisibile = useAppSelector(
    state => state.present.configuration.isOmnichannelAndPricesVisibile,
  )
  const isOmnichannelVisible = useAppSelector(state => state.present.configuration.isOmnichannelVisible)
  const [seasonListSectionOpen, setSeasonListSectionOpen] = useState(true)
  const [loadingDiscount, setLoadingDiscount] = useState(false)
  const omnichannelBodyRef = useRef()

  const getInitialTypeOfChannel = () => {

    if (marketplace) {
      return TypeOfChannel.Marketplace
    }
    if (sites) {
      return TypeOfChannel.Site
    }
    if (stores) {
      return TypeOfChannel.Store
    }
 
    const workspaceType = parseInt(checkDefaultTypeOfChannel(publicWorkspace))
    if (!isNaN(workspaceType)) {
      return workspaceType
    }

    return TypeOfChannel.Marketplace
  }

  useEffect(() => {
    if (isOmnichannelAndPricesVisibile) {
      dispatch(setTypeOfChannel(getInitialTypeOfChannel()))
    }
  }, [isOmnichannelAndPricesVisibile, publicWorkspace, sites, stores, marketplace, dispatch])

  useEffect(() => {
    dispatch(getSeasonRules())
    dispatch(getSeasons())
    dispatch(getChannelFilters())
    dispatch(getLocks())
    const interval = window.setInterval(() => {
      dispatch(getLocks())
    }, 10000)
    return () => {
      window.clearInterval(interval)
    }
  }, [dispatch, typeOfChannel])

  useEffect(() => {
    setLoadingDiscount(true)
    getInfoByChannelType(typeOfChannel, dispatch, () => {
      setLoadingDiscount(false)
    })
  }, [dispatch, typeOfChannel])

  const generateTypeOfChannelMenu = useMemo(() => {
    const types = Object.values(TypeOfChannel)
      .filter(v => !isNaN(Number(v)))
      .filter(x => (!sites ? x !== TypeOfChannel.Site : true))
      .filter(x => (!stores ? x !== TypeOfChannel.Store : true))
      .filter(x => (!marketplace ? x !== TypeOfChannel.Marketplace : true))
      .map(value => {
        const Icon = iconDictionary[value]
        const id = String(value)
        return {
          label: (
            <>
              <div className='icon-container-tabs'>
                <Icon className={'prices-tab-icon'} />
              </div>
              <div className='discount-type-tab-label'>
                {renderTranslation(language, TranslationKeys['channelType' + TypeOfChannel[id]])}
              </div>
            </>
          ),
          key: id,
        }
      })
    return types
  }, [language, sites, stores, marketplace])

  return isOmnichannelVisible ? (
    <Content className='page-content omnichannel' id='page-content-omnichannel'>
      <Row className='h-100' wrap={false}>
        <Col
          xxl={5}
          xl={7}
          lg={9}
          md={12}
          sm={14}
          className={'omnichannel-sider sider-collapsible' + (seasonListSectionOpen ? ' open' : ' closed')}
        >
          {!isSingleTab ? (
            <div>
              <Tabs
                defaultActiveKey={typeOfChannel.toString()}
                activeKey={typeOfChannel.toString()}
                animated
                onChange={e => {
                  dispatch(setTypeOfChannel(parseInt(e)))
                  setLoadingDiscount(true)
                }}
                items={generateTypeOfChannelMenu}
              />
            </div>
          ) : null}
          <Spin spinning={loadingDiscount} className='max-h-100' size='large'>
            <SeasonRuleList seasonListSectionOpen={seasonListSectionOpen} />
          </Spin>
        </Col>

        <Col className='collapse-container'>
          {seasonListSectionOpen ? (
            <CaretLeftOutlined
              className='generic-icon icon-slide-close'
              onClick={e => {
                setSeasonListSectionOpen(false)
              }}
            />
          ) : (
            <CaretRightOutlined
              className='generic-icon icon-slide-open'
              onClick={e => {
                setSeasonListSectionOpen(true)
              }}
            />
          )}
        </Col>
        <Col
          ref={omnichannelBodyRef}
          flex={'auto'}
          className={'omnichannel-body' + (seasonListSectionOpen ? ' open' : ' closed')}
        >
          <Row className={'sticky-body'}>
            <Col span={24}>
              <Outlet context={omnichannelBodyRef} />
            </Col>
          </Row>
        </Col>
      </Row>
    </Content>
  ) : null
}

export default Omnichannel
